import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  DOCUMENT_ENTITY_TYPE_LABELS,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
} from "@/api/labels";
import {
  useCompleteVersion,
  useInitiateDocument,
} from "@/api/hooks/documents";
import { useLocation as useLocationHook } from "@/api/hooks/locations";
import { useInvestigator } from "@/api/hooks/investigators";
import { computeSha256, uploadToPresignedUrl } from "@/api/upload";
import type {
  DocumentEntityType,
  DocumentType,
  DocumentValidation,
} from "@/api/types";
import { DelegationLogReviewPanel } from "./DelegationLogReviewPanel";
import { DocumentValidationsSection } from "./DocumentValidationsSection";
import { GcpCertReviewPanel } from "./GcpCertReviewPanel";
import { RotationReviewPanel } from "./RotationReviewPanel";

const ALLOWED_TYPES = new Set<string>(ALLOWED_DOCUMENT_MIME_TYPES);

type Stage =
  | "idle"
  | "initiating"
  | "uploading"
  | "completing"
  | "review" // waiting for operator to override FAILED validations
  | "done";

interface Props {
  entityType: DocumentEntityType;
  entityId: string;
  initialDocType?: DocumentType;
  /** Called once the new Document + Version is fully committed AND all
   * validations are either PASSED or OVERRIDDEN. Receives the new
   * document_id so the caller can navigate, refetch, or close a drawer. */
  onComplete: (documentId: string) => void;
  onCancel: () => void;
}

/** Reusable upload form. Rendered both standalone (DocumentUpload page)
 * and inside a Drawer (DocumentUploadDrawer). Owns the
 * presigned-PUT-then-complete protocol so the caller doesn't have to. */
export function DocumentUploadForm({
  entityType,
  entityId,
  initialDocType = "other",
  onComplete,
  onCancel,
}: Props) {
  const initiate = useInitiateDocument();
  const complete = useCompleteVersion();

  // Resolve a human label for the target entity.
  const locationDetail = useLocationHook(
    entityType === "location" ? entityId : undefined,
  );
  const investigatorDetail = useInvestigator(
    entityType === "investigator" ? entityId : undefined,
  );
  let entityName: string | null = null;
  if (entityType === "location" && locationDetail.data) {
    const loc = locationDetail.data;
    entityName = `${loc.display_id} · ${loc.name}`;
  } else if (entityType === "investigator" && investigatorDetail.data) {
    const inv = investigatorDetail.data;
    entityName = `${inv.first_name} ${inv.last_name}`;
  }

  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState<DocumentType>(initialDocType);
  // When the operator picks a defined doc type, the title is derived
  // from the type label — we hide the Title field and auto-fill it.
  // The "other" type keeps the manual title field so a free-form
  // catch-all upload still gets a name. Version number isn't baked
  // into the stored title (the document carries it separately and
  // every display surface renders ``<title> · v<n>`` alongside).
  useEffect(() => {
    if (docType === "other") {
      // Wipe any auto-filled title when the operator switches back
      // to "other" so they don't accidentally ship a stale label.
      setTitle((cur) => (cur === DOCUMENT_TYPE_LABELS[docType] ? "" : cur));
      return;
    }
    setTitle(DOCUMENT_TYPE_LABELS[docType]);
  }, [docType]);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [completedDocumentId, setCompletedDocumentId] = useState<string | null>(
    null,
  );
  const [completedVersionId, setCompletedVersionId] = useState<string | null>(
    null,
  );
  const [completedVersionMeta, setCompletedVersionMeta] = useState<{
    content_type: string;
    version_number: number;
  } | null>(null);
  const [validations, setValidations] = useState<DocumentValidation[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TYPES.has(f.type)) {
      setError(`Unsupported file type: ${f.type || "unknown"}`);
      setFile(null);
      return;
    }
    if (f.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      setError(`File too large (max ${MAX_DOCUMENT_FILE_SIZE_BYTES / 1024 / 1024} MB)`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file || !title.trim()) return;
    setError(null);
    try {
      setStage("initiating");
      setProgress(0);
      const presigned = await initiate.mutateAsync({
        entity_type: entityType,
        entity_id: entityId,
        title: title.trim(),
        document_type: docType,
        notes: notes.trim() || null,
        content_type: file.type,
        file_name: file.name,
        reason: reason.trim() || null,
      });

      setStage("uploading");
      const sha256 = await computeSha256(file);
      await uploadToPresignedUrl(
        file,
        presigned.upload_url,
        presigned.required_headers,
        (fraction) => setProgress(fraction),
      );

      setStage("completing");
      const result = await complete.mutateAsync({
        documentId: presigned.document_id,
        versionId: presigned.version_id,
        payload: { sha256, file_size_bytes: file.size },
      });

      setCompletedDocumentId(presigned.document_id);
      setCompletedVersionId(presigned.version_id);
      setCompletedVersionMeta({
        content_type: result.version.content_type,
        version_number: result.version.version_number,
      });
      setValidations(result.validations);
      if (
        result.validations.some(
          (v) =>
        v.status === "failed" ||
        v.status === "needs_review" ||
        v.status === "error",
        )
      ) {
        setStage("review");
        return;
      }
      setStage("done");
      onComplete(presigned.document_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
      setStage("idle");
    }
  };

  const onValidationsResolved = (next: DocumentValidation[]) => {
    setValidations(next);
    const stillBlocking = next.some(
      (v) =>
        v.status === "failed" ||
        v.status === "needs_review" ||
        v.status === "error",
    );
    if (!stillBlocking && completedDocumentId) {
      setStage("done");
      onComplete(completedDocumentId);
    }
  };

  const orientationNeedsReview = validations.find(
    (v) => v.validator_id === "pdf.orientation_check" && v.status === "needs_review",
  );
  const delegationLogNeedsReview = validations.find(
    (v) => v.validator_id === "delegation_log.review" && v.status === "needs_review",
  );
  const otherNeedsReview = validations.find(
    (v) =>
      v.validator_id !== "pdf.orientation_check" &&
      v.validator_id !== "delegation_log.review" &&
      v.status === "needs_review",
  );

  const busy = stage === "initiating" || stage === "uploading" || stage === "completing";
  const inReview = stage === "review";

  // After rotation correction the backend created v2 + re-ran content
  // validators on it. We pivot the form to v2 so subsequent review
  // panels (delegation log, GCP, …) operate on the corrected file
  // instead of closing the drawer.
  const onRotationApplied = (
    newVersion: import("@/api/types").DocumentVersion,
    nextValidations: DocumentValidation[],
  ) => {
    setCompletedVersionId(newVersion.id);
    setCompletedVersionMeta({
      content_type: newVersion.content_type,
      version_number: newVersion.version_number,
    });
    setValidations(nextValidations);
    const stillBlocking = nextValidations.some(
      (v) =>
        v.status === "failed" ||
        v.status === "needs_review" ||
        v.status === "error",
    );
    if (stillBlocking) {
      return;
    }
    setStage("done");
    if (completedDocumentId) onComplete(completedDocumentId);
  };

  if (
    inReview &&
    orientationNeedsReview &&
    completedDocumentId &&
    completedVersionId
  ) {
    return (
      <RotationReviewPanel
        documentId={completedDocumentId}
        versionId={completedVersionId}
        validation={orientationNeedsReview}
        onApplied={onRotationApplied}
        onCancel={onCancel}
      />
    );
  }

  if (
    inReview &&
    delegationLogNeedsReview &&
    completedDocumentId &&
    completedVersionId &&
    completedVersionMeta
  ) {
    return (
      <DelegationLogReviewPanel
        documentId={completedDocumentId}
        versionId={completedVersionId}
        contentType={completedVersionMeta.content_type}
        versionLabel={`v${completedVersionMeta.version_number}`}
        validation={delegationLogNeedsReview}
        onConfirmed={(updated) =>
          onValidationsResolved(
            validations.map((v) => (v.id === updated.id ? updated : v)),
          )
        }
        onCancel={onCancel}
      />
    );
  }

  if (
    inReview &&
    otherNeedsReview &&
    completedDocumentId &&
    completedVersionId &&
    completedVersionMeta
  ) {
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Review the extracted fields against the document on the left.
          Correct anything Bedrock got wrong, then confirm to record the
          credential.
        </Alert>
        <GcpCertReviewPanel
          documentId={completedDocumentId}
          versionId={completedVersionId}
          contentType={completedVersionMeta.content_type}
          versionLabel={`v${completedVersionMeta.version_number}`}
          validation={otherNeedsReview}
          onConfirmed={(updated) =>
            onValidationsResolved(
              validations.map((v) => (v.id === updated.id ? updated : v)),
            )
          }
          onCancel={onCancel}
        />
      </Stack>
    );
  }

  if (inReview && completedDocumentId) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          Upload completed, but one or more validations failed. Review
          the issue below — either mark it reviewed with a reason, or
          cancel and upload a corrected file.
        </Alert>
        <DocumentValidationsSection
          documentId={completedDocumentId}
          validations={validations}
          onOverridden={(updated) =>
            onValidationsResolved(
              validations.map((v) => (v.id === updated.id ? updated : v)),
            )
          }
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onCancel}>Done</Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="body2" color="text.secondary">
        Attaching to:{" "}
        <strong>{entityName ?? DOCUMENT_ENTITY_TYPE_LABELS[entityType]}</strong>
        {entityName && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            ({DOCUMENT_ENTITY_TYPE_LABELS[entityType]})
          </Typography>
        )}
      </Typography>
      <TextField
        select
        label="Type"
        value={docType}
        onChange={(e) => setDocType(e.target.value as DocumentType)}
        required
      >
        {DOCUMENT_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {DOCUMENT_TYPE_LABELS[t]}
          </MenuItem>
        ))}
      </TextField>
      {/* Defined doc types derive their title from the type label;
       * only the "other" catch-all needs a manual title. */}
      {docType === "other" && (
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          helperText="A short name — version number is tracked separately."
        />
      )}
      <TextField
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        rows={2}
      />
      <TextField
        label="Reason (optional for first upload)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="Stored on the audit log alongside this upload event."
      />

      <Box
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          handleFile(f ?? null);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: "divider",
          borderRadius: 2,
          py: 4,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: "background.default",
        }}
      >
        {file ? (
          <Typography>
            <strong>{file.name}</strong> · {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
        ) : (
          <Typography color="text.secondary">
            Drag a file here, or click to choose
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ALLOWED_DOCUMENT_MIME_TYPES.join(",")}
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        />
      </Box>

      {busy && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {stage === "initiating" && "Preparing upload…"}
            {stage === "uploading" && `Uploading… ${Math.round(progress * 100)}%`}
            {stage === "completing" && "Finalizing…"}
          </Typography>
          <LinearProgress
            variant={stage === "uploading" ? "determinate" : "indeterminate"}
            value={progress * 100}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!file || !title.trim() || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Upload
        </Button>
      </Box>
    </Stack>
  );
}
